import { useCallback, useEffect, useMemo } from "react";
import { oids } from "@/configs/Settings";
import { useManagerContext } from "@/contexts/ManagerContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { usePaymentNoticeStats } from "@/services/reports/paymentnotice/hooks/usePaymentNoticeStats";
import { useAppointmentStats } from "@/services/fhir/statistics/hooks/useAppointmentStats";
import type {
  ConsultationsBillingMetrics,
  PaymentDetailData,
} from "../types/consultationsBilling.types";
import {
  isActionablePendingAppointment,
  isExpiredPendingAppointment,
  isOverdueBookedAppointment,
  isStalePendingPayment,
} from "../utils/consultationSituationLabels";
import {
  getChargeAmount,
  getPaymentNoticeMonetaryValue,
} from "../utils/getChargeAmount";
import { mergeAppointmentsById, resolveAppointmentForPaymentContext } from "../utils/matchConsultationToPayment";

function findScheduleBySlot(slot: fhir5.Slot, schedules: any[]) {
  if (!slot?.schedule?.reference) return null;
  const scheduleId = slot.schedule.reference.split("/")[1];
  return (
    schedules?.find((schedule: any) => schedule?.resource?.id === scheduleId)
      ?.resource || null
  );
}

export function useConsultationsBillingData(dateRange?: {
  startDate?: string;
  endDate?: string;
}) {
  const { practitioner } = usePractitionerRoleContext();
  const { location } = useLocationContext();
  const { displaySchedules } = useManagerContext();

  const actors = useMemo(
    () =>
      location?.map((loc) => `Location/${loc.resource?.id}`).filter(Boolean) ??
      [],
    [location]
  );

  const {
    data: pendingListData,
    loading: loadingPendingList,
    fetchStats: fetchPendingList,
  } = useAppointmentStats({
    actors,
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    status: ["pending"],
    summary: false,
    count: 1000,
  });

  const {
    data: bookedListData,
    loading: loadingBookedList,
    fetchStats: fetchBookedList,
  } = useAppointmentStats({
    actors,
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    status: ["booked"],
    summary: false,
    count: 1000,
  });

  const {
    data: fulfilledData,
    loading: loadingFulfilled,
    fetchStats: fetchFulfilled,
  } = useAppointmentStats({
    actors,
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    status: ["fulfilled"],
    summary: true,
  });

  const {
    data: cancelledData,
    loading: loadingCancelled,
    fetchStats: fetchCancelled,
  } = useAppointmentStats({
    actors,
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    status: ["cancelled"],
    summary: true,
  });

  const {
    data: {
      paymentNotices: clearedPayments,
      patients: clearedPatients,
      slots: clearedSlots,
    },
    loading: loadingCleared,
    fetchStats: fetchClearedStats,
  } = usePaymentNoticeStats({
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    paymentStatus: "cleared",
  });

  const {
    data: {
      paymentNotices: pendingPayments,
      patients: pendingPatients,
      slots: pendingSlots,
      appointments: pendingAppointments,
    },
    loading: loadingPendingPayments,
    fetchStats: fetchPendingStats,
  } = usePaymentNoticeStats({
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    paymentStatus: "missing",
  });

  const linkedPendingAppointments = useMemo(
    () =>
      mergeAppointmentsById(
        pendingAppointments,
        pendingListData?.appointments ?? []
      ),
    [pendingAppointments, pendingListData?.appointments]
  );

  const enrichedActivePayments = useMemo((): PaymentDetailData[] => {
    const allActive = pendingPayments.filter(
      (payment) => payment.status === "active"
    );

    return allActive.map((payment) => {
      const slotRef = payment.request?.reference;
      const slot = slotRef
        ? pendingSlots.find((entry) => `Slot/${entry.id}` === slotRef) ?? null
        : null;
      const schedule = slot ? findScheduleBySlot(slot, displaySchedules) : null;
      const patientRef = payment.response?.reference;
      const patient = patientRef
        ? pendingPatients.find((entry) => `Patient/${entry.id}` === patientRef) ??
          null
        : null;
      const appointment = resolveAppointmentForPaymentContext(
        { payment, slot, appointment: null },
        linkedPendingAppointments
      );
      const practitionerRef = payment.reporter?.reference;
      const practitionerId = practitionerRef?.split("-").pop();
      const practitionerResource = practitionerId
        ? practitioner?.find((entry) =>
            entry.resource?.identifier?.some(
              (id) => id.system?.includes(oids.cpf) && id.value === practitionerId
            )
          )?.resource ?? null
        : null;
      const locationIdentifier = payment.identifier?.find((id) =>
        id.system?.includes("location")
      )?.value;
      const locationId = locationIdentifier?.split("/").pop();
      const locationResource = locationId
        ? location?.find((entry) => entry.resource?.id === locationId)?.resource ??
          null
        : null;

      return {
        payment,
        patient,
        practitioner: practitionerResource,
        location: locationResource,
        slot,
        schedule,
        appointment: appointment ?? null,
      };
    });
  }, [
    pendingPayments,
    pendingSlots,
    pendingPatients,
    linkedPendingAppointments,
    displaySchedules,
    practitioner,
    location,
  ]);

  const { actionableActivePayments, staleActivePayments, cancelledPayments } =
    useMemo(() => {
      const actionable: PaymentDetailData[] = [];
      const stale: PaymentDetailData[] = [];
      const cancelledRaw = pendingPayments.filter(
        (payment) => payment.status === "cancelled"
      );

      enrichedActivePayments.forEach((paymentData) => {
        if (isStalePendingPayment(paymentData)) stale.push(paymentData);
        else actionable.push(paymentData);
      });

      const enrichCancelled = cancelledRaw.map((payment): PaymentDetailData => {
        const slotRef = payment.request?.reference;
        const slot = slotRef
          ? pendingSlots.find((entry) => `Slot/${entry.id}` === slotRef) ?? null
          : null;
        const schedule = slot ? findScheduleBySlot(slot, displaySchedules) : null;
        const patientRef = payment.response?.reference;
        const patient = patientRef
          ? pendingPatients.find((entry) => `Patient/${entry.id}` === patientRef) ??
            null
          : null;
        return {
          payment,
          patient,
          practitioner: null,
          location: null,
          slot,
          schedule,
          appointment: null,
        };
      });

      return {
        actionableActivePayments: actionable,
        staleActivePayments: stale,
        cancelledPayments: enrichCancelled,
      };
    }, [
      enrichedActivePayments,
      pendingPayments,
      pendingSlots,
      pendingPatients,
      displaySchedules,
    ]);

  const financialTotals = useMemo(
    () => ({
      pendingAmount: actionableActivePayments.reduce(
        (sum, entry) => sum + getChargeAmount(entry),
        0
      ),
      receivedAmount: clearedPayments.reduce(
        (sum, payment) => sum + getPaymentNoticeMonetaryValue(payment),
        0
      ),
      closedAmount:
        staleActivePayments.reduce(
          (sum, entry) => sum + getChargeAmount(entry),
          0
        ) +
        cancelledPayments.reduce(
          (sum, entry) => sum + getChargeAmount(entry),
          0
        ),
    }),
    [
      actionableActivePayments,
      clearedPayments,
      staleActivePayments,
      cancelledPayments,
    ]
  );

  const pendingSplit = useMemo(() => {
    let actionablePending = 0;
    let expiredPending = 0;
    (pendingListData?.appointments ?? []).forEach((appointment) => {
      if (isExpiredPendingAppointment(appointment)) expiredPending += 1;
      else if (isActionablePendingAppointment(appointment)) actionablePending += 1;
    });
    return { actionablePending, expiredPending };
  }, [pendingListData?.appointments]);

  const bookedSplit = useMemo(() => {
    let overdueBooked = 0;
    let upcomingBooked = 0;
    (bookedListData?.appointments ?? []).forEach((appointment) => {
      if (isOverdueBookedAppointment(appointment)) overdueBooked += 1;
      else upcomingBooked += 1;
    });
    return { overdueBooked, upcomingBooked };
  }, [bookedListData?.appointments]);

  const metrics = useMemo<ConsultationsBillingMetrics>(() => {
    const booked = bookedSplit.upcomingBooked + pendingSplit.actionablePending;
    const overdueBooked = bookedSplit.overdueBooked;
    const fulfilled = fulfilledData?.total || 0;
    const cancelledAppointments = cancelledData?.total || 0;
    const closed =
      cancelledAppointments +
      pendingSplit.expiredPending +
      staleActivePayments.length +
      cancelledPayments.length;

    return {
      booked,
      overdueBooked,
      fulfilled,
      closed,
      total: booked + overdueBooked + fulfilled + closed,
      pendingAmount: financialTotals.pendingAmount,
      receivedAmount: financialTotals.receivedAmount,
      closedAmount: financialTotals.closedAmount,
    };
  }, [
    bookedSplit,
    pendingSplit,
    fulfilledData,
    cancelledData,
    staleActivePayments.length,
    cancelledPayments.length,
    financialTotals,
  ]);

  const refetchAll = useCallback(async () => {
    if (actors.length > 0) {
      await Promise.all([fetchPendingList(), fetchBookedList()]);
    }
    await Promise.all([
      fetchFulfilled(),
      fetchCancelled(),
      fetchPendingStats(),
      fetchClearedStats(),
    ]);
  }, [
    actors,
    fetchPendingList,
    fetchBookedList,
    fetchFulfilled,
    fetchCancelled,
    fetchPendingStats,
    fetchClearedStats,
  ]);

  useEffect(() => {
    if (actors.length === 0) return;
    fetchPendingList();
    fetchBookedList();
    fetchFulfilled();
    fetchCancelled();
  }, [
    actors,
    dateRange?.startDate,
    dateRange?.endDate,
    fetchPendingList,
    fetchBookedList,
    fetchFulfilled,
    fetchCancelled,
  ]);

  const loading =
    loadingPendingList ||
    loadingBookedList ||
    loadingFulfilled ||
    loadingCancelled ||
    loadingPendingPayments ||
    loadingCleared;

  return {
    actors,
    loading,
    metrics,
    pendingListData,
    bookedListData,
    pendingPatients: pendingListData?.patients ?? [],
    bookedPatients: bookedListData?.patients ?? [],
    linkedPendingAppointments,
    activePaymentsEnriched: enrichedActivePayments,
    pendingSlots,
    financialTotals,
    fetchPendingList,
    fetchBookedList,
    refetchPayments: refetchAll,
  };
}
